import { Cell, Column, Row, ValueType } from '../types';

/**
 * 행이 비어있는지 판단하는 함수
 * 
 * @param row - 검사할 행 객체
 * @returns boolean - 빈 행 여부
 * 
 * @description
 * 1. isEmpty 플래그가 있으면 우선 사용
 * 2. isEmpty 플래그가 없는 경우 fallback 로직 적용
 *    - ID 필드들 체크 (xxId 패턴)
 *    - 핵심 데이터 필드들이 모두 비어있는지 체크
 */
export const isEmptyRow = (row: Row): boolean => {
    // 1. isEmpty 플래그가 있으면 우선 사용
    if (row.data.isEmpty?.value === true) {
        return true;
    }

    // 2. isEmpty 플래그가 없는 경우 fallback 로직
    if (row.data.isEmpty === undefined) {
        const data = row.data;

        // ID 필드들 체크 (xxId 패턴)
        const idFields = Object.keys(data).filter(key =>
            key.toLowerCase().endsWith('id') &&
            key !== 'checkbox'
        );

        // ID가 비어있으면 빈 행
        if (idFields.length > 0) {
            const hasEmptyId = idFields.some(field => {
                const value = data[field]?.value;
                return value === '' || value === null || value === undefined;
            });
            if (hasEmptyId) return true;
        }

        // 핵심 데이터 필드들이 모두 비어있으면 빈 행
        const coreFields = ['itemName', 'splAmt', 'amount', 'qty', 'name', 'description'];
        const existingCoreFields = coreFields.filter(field => data[field]);
        if (existingCoreFields.length > 0) {
            return existingCoreFields.every(field => {
                const value = data[field]?.value;
                return value === '' ||
                    value === null ||
                    value === undefined ||
                    (typeof value === 'string' && value.trim() === '');
            });
        }
    }

    return false;
};

/**
 * 두 셀 값을 비교하는 함수
 * 
 * @param cellA - 첫 번째 셀 값
 * @param cellB - 두 번째 셀 값
 * @param valueType - 값의 타입
 * @returns number - 비교 결과 (-1, 0, 1)
 */
export const compareCellValues = (cellA: any, cellB: any, valueType: ValueType): number => {
    // 둘 다 null이거나 undefined인 경우
    if (cellA == null && cellB == null) return 0;
    // cellA가 null이면 항상 뒤로
    if (cellA == null) return 1;
    // cellB가 null이면 항상 뒤로
    if (cellB == null) return -1;

    let comparison = 0;
    switch (valueType) {
        case ValueType.NUMBER:
            comparison = Number(cellA) - Number(cellB);
            break;
        case ValueType.PERCENT:
            // % 기호 제거하고 숫자로 변환
            const numA = Number(String(cellA).replace('%', ''));
            const numB = Number(String(cellB).replace('%', ''));
            comparison = numA - numB;
            break;
        case ValueType.DATE:
            comparison = new Date(cellA).getTime() - new Date(cellB).getTime();
            break;
        default: // string
            if (typeof cellA === 'string' && typeof cellB === 'string' &&
                (cellA.includes('%') || cellB.includes('%'))) {
                // % 기호가 있는 경우 숫자로 변환하여 비교
                const numA = Number(cellA.replace('%', ''));
                const numB = Number(cellB.replace('%', ''));
                comparison = numA - numB;
            } else {
                comparison = String(cellA).localeCompare(String(cellB));
            }
    }

    return comparison;
};

/**
 * 컬럼 타입에 따른 초기값을 반환하는 함수
 * 
 * @param valueType - 컬럼의 값 타입
 * @returns any - 해당 타입의 초기값
 */
export const getInitialValueByType = (valueType: ValueType): any => {
    switch (valueType) {
        case ValueType.NUMBER:
            return 0;
        case ValueType.BOOLEAN:
            return false;
        case ValueType.DATE:
            return new Date().toISOString();
        case ValueType.STRING:
        case ValueType.CHECKBOX:
        default:
            return '';
    }
};

/**
 * 행 데이터에서 빈 값을 기본값으로 채우는 함수
 * 
 * @param rowData - 원본 행 데이터
 * @returns Row['data'] - 기본값이 채워진 행 데이터
 */
export const fillEmptyValuesWithDefaults = (rowData: Row['data']): Row['data'] => {
    return Object.entries(rowData).reduce((acc, [key, cell]) => {
        const isEmpty = cell.value === undefined || cell.value === null || cell.value === '';

        // value가 비어 있고, 기존 타입이 number인 경우 0, 아니면 ''
        const defaultValue = isEmpty
            ? typeof cell.value === 'number' ? 0 : ''
            : cell.value;

        acc[key] = { ...cell, value: defaultValue };
        return acc;
    }, {} as Row['data']);
};

/**
 * 컬럼 정보를 기반으로 빈 행 데이터를 생성하는 함수
 * 
 * @param columns - 컬럼 정보 객체
 * @returns Row['data'] - 생성된 빈 행 데이터
 */
export const createEmptyRowData = (columns: { [columnId: string]: Column }): Row['data'] => {
    const emptyData: { [columnId: string]: Cell } = {};

    // isEmpty 플래그 자동 추가
    emptyData['isEmpty'] = { value: true };

    Object.values(columns).forEach((column) => {
        const initialValue = getInitialValueByType(column.type);
        emptyData[column.id] = { value: initialValue };
    });

    return emptyData;
};